/**
 * Czysta logika Wyścigu Piesków (WP12c, PLAN §14).
 * Bez Phaser — unit-testowalna. Brak fail-state; gwiazdki tylko rosną.
 */
import { MINIGAMES } from '@pieskowo/shared';
import type { RunStatsClient } from '../../core/RunClient.js';

export const RUNNER_GAME_ID = 'runner' as const;
export const RUNNER_DURATION_MS = 60_000;
export const GOOD_STARS_THRESHOLD = MINIGAMES.runner.goodStarsThreshold; // ≥8
export const MAX_STARS = MINIGAMES.runner.maxStars; // 16

export const GRAVITY = 2400;
export const JUMP_VELOCITY = -820;
export const GROUND_Y = 520;
export const PLAYER_X = 200;
export const PLAYER_W = 72;
export const PLAYER_H = 72;
export const SCROLL_SPEED_BASE = 300;
export const SCROLL_SPEED_MAX = 460;
export const SHAKE_MS = 550;
export const INVULN_MS = 700;

export type EntityKind = 'obstacle' | 'bone';

export type RunnerEntity = {
  id: number;
  kind: EntityKind;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Kość zebrana (zniknęła). */
  collected: boolean;
  /** Przeszkoda już „otrzepała” gracza w tym przejściu. */
  hit: boolean;
};

export type RunnerPhase = 'playing' | 'finished';

export type RunnerState = {
  phase: RunnerPhase;
  elapsedMs: number;
  stars: number;
  score: number;
  taps: number;
  playerY: number;
  playerVy: number;
  onGround: boolean;
  /** Do tego czasu (elapsedMs) trwa otrzepanie / invuln. */
  shakeUntilMs: number;
  invulnUntilMs: number;
  entities: RunnerEntity[];
  nextSpawnAtMs: number;
  nextId: number;
  scrollSpeed: number;
  worldW: number;
};

export type RunnerInitOpts = {
  worldW?: number;
  durationMs?: number;
  /** Deterministyczny RNG [0,1) — testy. */
  rng?: () => number;
};

function defaultRng(): number {
  return Math.random();
}

export function createRunnerState(opts: RunnerInitOpts = {}): RunnerState {
  return {
    phase: 'playing',
    elapsedMs: 0,
    stars: 0,
    score: 0,
    taps: 0,
    playerY: GROUND_Y - PLAYER_H / 2,
    playerVy: 0,
    onGround: true,
    shakeUntilMs: 0,
    invulnUntilMs: 0,
    entities: [],
    nextSpawnAtMs: 900,
    nextId: 1,
    scrollSpeed: SCROLL_SPEED_BASE,
    worldW: opts.worldW ?? 1280,
  };
}

/** Czy gracz może skoczyć (na ziemi, gra trwa). */
export function canJump(state: RunnerState): boolean {
  return state.phase === 'playing' && state.onGround;
}

/** Tap / spacja = skok. Zwraca true gdy skok się udał. */
export function tryJump(state: RunnerState): boolean {
  if (!canJump(state)) return false;
  state.playerVy = JUMP_VELOCITY;
  state.onGround = false;
  state.taps += 1;
  return true;
}

/** Pozostały czas rundy (ms), nigdy ujemny. */
export function remainingMs(state: RunnerState, durationMs = RUNNER_DURATION_MS): number {
  return Math.max(0, durationMs - state.elapsedMs);
}

export function isShaking(state: RunnerState): boolean {
  return state.elapsedMs < state.shakeUntilMs;
}

export function isInvulnerable(state: RunnerState): boolean {
  return state.elapsedMs < state.invulnUntilMs;
}

/** Gwiazdki tylko rosną, cap z shared. */
export function addStars(state: RunnerState, n: number): void {
  if (n <= 0) return;
  state.stars = Math.min(MAX_STARS, state.stars + Math.floor(n));
}

function aabbOverlap(
  ax: number,
  ay: number,
  aw: number,
  ah: number,
  bx: number,
  by: number,
  bw: number,
  bh: number,
): boolean {
  const aLeft = ax - aw / 2;
  const aRight = ax + aw / 2;
  const aTop = ay - ah / 2;
  const aBottom = ay + ah / 2;
  const bLeft = bx - bw / 2;
  const bRight = bx + bw / 2;
  const bTop = by - bh / 2;
  const bBottom = by + bh / 2;
  return aLeft < bRight && aRight > bLeft && aTop < bBottom && aBottom > bTop;
}

function spawnEntity(state: RunnerState, rng: () => number): void {
  const id = state.nextId++;
  const roll = rng();
  // ~55% kości, ~45% przeszkód — przyjazne tempo zbierania
  if (roll < 0.55) {
    const boneY = GROUND_Y - PLAYER_H / 2 - 20 - rng() * 140;
    state.entities.push({
      id,
      kind: 'bone',
      x: state.worldW + 40,
      y: boneY,
      w: 48,
      h: 48,
      collected: false,
      hit: false,
    });
  } else {
    const h = 48 + Math.floor(rng() * 36);
    state.entities.push({
      id,
      kind: 'obstacle',
      x: state.worldW + 50,
      y: GROUND_Y - h / 2,
      w: 52 + Math.floor(rng() * 20),
      h,
      collected: false,
      hit: false,
    });
  }
  // następny spawn 0.7–1.6 s, lekko gęściej z czasem
  const progress = Math.min(1, state.elapsedMs / RUNNER_DURATION_MS);
  const gap = 700 + rng() * 900 - progress * 250;
  state.nextSpawnAtMs = state.elapsedMs + Math.max(520, gap);
}

/**
 * Jeden krok symulacji. Zwraca zdarzenia do FX w scenie.
 * NIGDY nie ustawia fail — tylko playing → finished po czasie lub forceFinish.
 */
export function tick(
  state: RunnerState,
  dtMs: number,
  opts: { durationMs?: number; rng?: () => number; forceFinish?: boolean } = {},
): { collectedBoneIds: number[]; obstacleHits: number[] } {
  const collectedBoneIds: number[] = [];
  const obstacleHits: number[] = [];
  if (state.phase !== 'playing') {
    return { collectedBoneIds, obstacleHits };
  }

  const durationMs = opts.durationMs ?? RUNNER_DURATION_MS;
  const rng = opts.rng ?? defaultRng;
  const dt = Math.max(0, Math.min(dtMs, 50)) / 1000; // cap 50 ms frame
  state.elapsedMs += Math.max(0, Math.min(dtMs, 50));

  // delikatne przyspieszenie
  const progress = Math.min(1, state.elapsedMs / durationMs);
  state.scrollSpeed =
    SCROLL_SPEED_BASE + (SCROLL_SPEED_MAX - SCROLL_SPEED_BASE) * progress;

  // fizyka skoku
  if (!state.onGround) {
    state.playerVy += GRAVITY * dt;
    state.playerY += state.playerVy * dt;
    const groundCenter = GROUND_Y - PLAYER_H / 2;
    if (state.playerY >= groundCenter) {
      state.playerY = groundCenter;
      state.playerVy = 0;
      state.onGround = true;
    }
  }

  // spawn
  if (state.elapsedMs >= state.nextSpawnAtMs) {
    spawnEntity(state, rng);
  }

  // scroll + kolizje
  const px = PLAYER_X;
  const py = state.playerY;
  const invuln = isInvulnerable(state);

  for (const e of state.entities) {
    if (e.collected) continue;
    e.x -= state.scrollSpeed * dt;

    if (
      !aabbOverlap(px, py, PLAYER_W * 0.7, PLAYER_H * 0.7, e.x, e.y, e.w, e.h)
    ) {
      continue;
    }

    if (e.kind === 'bone') {
      e.collected = true;
      addStars(state, 1);
      collectedBoneIds.push(e.id);
    } else if (e.kind === 'obstacle' && !e.hit && !invuln) {
      e.hit = true;
      state.shakeUntilMs = state.elapsedMs + SHAKE_MS;
      state.invulnUntilMs = state.elapsedMs + INVULN_MS;
      obstacleHits.push(e.id);
      // gwiazdek NIE odejmujemy — tylko otrzepanie
    }
  }

  // score ~ dystans
  state.score = Math.floor(state.elapsedMs * 0.05 + state.stars * 10);

  // posprzątaj poza ekranem
  state.entities = state.entities.filter((e) => e.x > -80 && !e.collected);

  if (opts.forceFinish || state.elapsedMs >= durationMs) {
    state.phase = 'finished';
    state.elapsedMs = Math.min(state.elapsedMs, durationMs);
  }

  return { collectedBoneIds, obstacleHits };
}

/** Stats do RunClient.finishRun — nagrody liczy SERWER, nie my. */
export function buildRunStats(state: RunnerState): RunStatsClient {
  return {
    stars: Math.max(0, Math.min(MAX_STARS, Math.floor(state.stars))),
    score: Math.max(0, Math.floor(state.score)),
    durationMs: Math.max(0, Math.floor(state.elapsedMs)),
    taps: Math.max(0, Math.floor(state.taps)),
  };
}

/** Czy wynik wizualnie „dobra gra” (tylko UI/hint — nagroda z serwera). */
export function isGoodRun(stars: number): boolean {
  return stars >= GOOD_STARS_THRESHOLD;
}

export function returnSceneForEntry(
  entryPoint?: string,
): 'ConsoleHub' | 'Town' {
  return entryPoint === 'console' ? 'ConsoleHub' : 'Town';
}
